/**
 * Currently stores auth utility functions
 * This file will hold our future auth api implementation when we set it up on our backend.
 *
 * Copyright (c) 2018 Propellant Software Corp
 * Author: Kelvin Nguyen
 */
import queryString from 'query-string'
import isString from 'lodash/isString'
import firebaseUtils from '../../utils/firebase'
import { resetUser } from '../../utils/tracking'
import RouteMap, { isGuestBoardRoute } from 'common/configs/routeMap'
import { GUEST_QUERY, GUEST_AUTH_ACTION } from 'client/app/js/enums/guestQueryParams'

const TOKEN_NAME = 'userToken'

// Should match api server
export const Headers = {
  Authorization: 'Authorization',
  ModifiedSince: 'X-Modified-Since'
}

export const storeToken = (token) => {
  window.sessionStorage.setItem(TOKEN_NAME, token)
}

export const getToken = () => {
  return window.sessionStorage.getItem(TOKEN_NAME)
}

export const removeToken = () => {
  return window.sessionStorage.removeItem(TOKEN_NAME)
}

export const getHeaders = (options = {}) => {
  const headers = {}

  const token = options.userToken || getToken()
  if (token) {
    headers[Headers.Authorization] = `Bearer ${token}`
  }

  const { modifiedSince } = options
  if (modifiedSince) {
    headers[Headers.ModifiedSince] = modifiedSince
  }

  return headers
}

export const getAndSetUserToken = () => {
  return firebaseUtils.getIdToken()
    .then((token) => {
      if (token) {
        storeToken(token)
      }

      return token
    })
}

/**
 * Signify guest auth action by inserting "guest=authAction" query string
 * @param {Function} options.navigate Uses the navigate function instead of window.location
 */
export const guestAuthAction = (options = {}) => {
  const queries = queryString.parse(window.location.search)
  const updatedQueries = {
    ...queries,
    [GUEST_QUERY]: [GUEST_AUTH_ACTION]
  }
  const updatedQueryStr = `?${queryString.stringify(updatedQueries)}`

  if (options.navigate) {
    options.navigate(updatedQueryStr, true)
  } else {
    window.history.replaceState(null, null, updatedQueryStr)
  }
}

/**
 * Force a redirect to login page
 * @param {String} fromUrl The url to add to query string
 * @param {Boolean} replace Sepcifies whether to replace the current url
 * @param {Boolean} options.windowOpen Opens a new tab/window
 * @param {Function} options.forceGuestSignup Redirect to signup page instead of opening modal
 * @param {Function} options.navigate Uses navigate function instead of window.location
 */
export const forceAuthRedirect = (fromUrl = '', replace = true, options = {}) => {
  // Any auth-based action triggered in the guest board will open a modal
  // This is a temporary solution that is the least invasive for our codebase
  const isGuest = isGuestBoardRoute()
  if (isGuest && !options.forceGuestSignup) {
    guestAuthAction(options)

    return
  }

  let pathname = ''
  // Prevent rendering [object Object] if we pass a non-string value by mistake,
  if (isString(fromUrl)) {
    pathname = fromUrl
  } else if (window.location.pathname) {
    pathname = window.location.pathname
  }

  const redirectUrl = encodeURIComponent(pathname)
  const accountRoutes = RouteMap.account
  const queryStr = redirectUrl ? `?from=${redirectUrl}` : ''

  const url = isGuest
    ? `${accountRoutes.url}${accountRoutes.signup}`
    : `${accountRoutes.url}${accountRoutes.login}${queryStr}`

  resetUser()
  removeToken()

  // Embed routes should treat links different and behave differently
  // This is a temporary solution in lieu of writing a brand new component system for embeds
  const isEmbedRoute = window.location.pathname.startsWith(RouteMap.embed.url)
  if (options.windowOpen || isEmbedRoute) {
    window.open(url)
  } else if ((isGuest && options.forceGuestSignup)) {
    // Preserve guest board back link if the user came from the guest board
    window.location.assign(url)
  } else if (replace) {
    // If we do a force redirect, replace the current route, this prevents too many extra back links
    window.location.replace(url)
  } else {
    window.location = url
  }
}

export const logout = () => {
  resetUser()
  removeToken()

  firebaseUtils.logout().then(() => {
    window.location = '/'
  })
}

export default {
  getHeaders,
  storeToken,
  getToken,
  getAndSetUserToken,
  forceAuthRedirect,
  logout
}
